'use client';

import Link from 'next/link';
import { PrismicImage, SliceZone } from '@prismicio/react';
import { getComponents } from '@/slices/component-factory';
import React, { useEffect } from 'react';
import { CloseIcon, HamburgerIcon } from '@/assets';
import { useMenuContext } from '@/context/MenuContext';
import { useModalStore } from '@/store';
import Portal from './Portal';
import {
  Slice as PrismicSlice,
  ImageFieldImage,
  AnyRegularField,
} from '@prismicio/types';

interface CustomSlice
  extends PrismicSlice<
    string,
    Record<string, AnyRegularField>,
    Record<string, AnyRegularField>
  > {
  id: string;
  variation: string;
  primary: {
    tag?: string;
  };
}

export const GlobalNavBar = ({ data }: { data: Record<string, unknown> }) => {
  const { slices } = data as { slices: CustomSlice[] };
  const getStartedFormSlice = slices.find(
    (slice) => slice.slice_type === 'feature_card_grid',
  );
  const contactSlice = slices.filter(
    (slice) =>
      slice.slice_type === 'menu_link' && slice.variation === 'bordered',
  );
  // Exclude get_started_form and feature_card_grid from navigation rendering
  const linkSlices = slices.filter(
    (slice) =>
      slice.slice_type !== 'submenu' &&
      !(slice.slice_type === 'menu_link' && slice.variation === 'bordered') &&
      slice.slice_type !== 'get_started_form' &&
      slice.slice_type !== 'feature_card_grid',
  );
  const companyLogo = data?.company_logo as ImageFieldImage | null;
  const context: Record<string, unknown> = {};

  // Use full components for navbar (not blog-specific)
  const components = getComponents(false);

  linkSlices.forEach(({ primary, variation }) => {
    if (variation === 'withSubMenu') {
      context[primary.tag ?? 'default'] = slices.filter(
        (slice) =>
          slice.slice_type === 'submenu' &&
          primary.tag === (slice.primary.tag ?? 'default'),
      );
    }
  });
  const { isMobile, isMenuOpen, toggleSideBar } = useMenuContext();
  const { isOpen, closeModal } = useModalStore();

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  return (
    <div className="flex items-center justify-between w-full">
      <div className="flex justify-center mr-1.5 md:mr-0 md:flex-1 md:justify-start">
        <Link href="/" aria-label="Devsu Home Page">
          <PrismicImage
            field={companyLogo}
            className="h-6 w-auto md:h-8 object-contain"
            width={120}
            height={30}
            sizes="(max-width: 768px) 96px, 120px"
            style={{ width: 'auto', height: '2rem' }}
          />
        </Link>
      </div>

      <nav
        aria-label="Devsu main navigation"
        className="hidden lg:flex flex-1 justify-center"
      >
        <ul className="z-40 flex h-full items-center space-x-8 py-4">
          <SliceZone
            components={components}
            slices={linkSlices}
            context={context}
          />
        </ul>
      </nav>

      <div className="flex-1 flex justify-end">
        <div className="hidden lg:flex">
          <SliceZone components={components} slices={contactSlice} />
        </div>
      </div>

      {/*Sidebar Version*/}
      <div className="lg:hidden flex gap-4 items-center">
        {!isMenuOpen && (
          <SliceZone components={components} slices={contactSlice} />
        )}
        <button
          onClick={toggleSideBar}
          type="button"
          className="block lg:hidden"
          aria-label="Toggle Navigation Menu"
        >
          {isMenuOpen ? <CloseIcon /> : <HamburgerIcon />}
        </button>
        <section
          className={`${isMenuOpen ? 'visible' : 'invisible'} ${
            isMobile ? 'pb-60' : ''
          } overflow-y-auto overscroll-contain block fixed top-20 left-0 h-screen w-full bg-menu-gradient z-50 max-h-screen`}
        >
          <nav className="flex flex-col items-start justify-center [&>ul]:w-full gap-6 pb-24 [&>button]:ml-4 px-6">
            <ul>
              <SliceZone
                components={components}
                slices={linkSlices}
                context={context}
              />
            </ul>
          </nav>
        </section>
      </div>
      {/*Sidebar Version*/}

      {/* Full-Screen Modal for GetStartedForm */}
      {isOpen && getStartedFormSlice && (
        <Portal>
          <div className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-xl transition-all duration-500">
            <div className="relative h-full overflow-y-auto overscroll-contain">
              <button
                className="absolute top-6 right-6 z-[10000] bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full p-3 text-white"
                aria-label="Close popup"
                onClick={closeModal}
              >
                <CloseIcon width={32} height={32} />
              </button>
              <div className="min-h-full">
                <SliceZone
                  slices={[getStartedFormSlice]}
                  components={components}
                />
              </div>
            </div>
          </div>
        </Portal>
      )}
    </div>
  );
};
